import Cookies from "js-cookie";
import React, { FC, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BaseLayout from "../layouts/BaseLayout";
import Loader from "../components/elements/Loader";
import api from "../api";
import { ReduxApplicationState } from "../types/ReduxTypes/ReduxApplicationState";
import { SentryRoute } from "../sentry";
import { setAgency } from "../redux/reducers/agency";
import { setSession } from "../redux/reducers/session";
import { setUser } from "../redux/reducers/user";

type AuthGuardRouteProps = {
  exact?: boolean;
  path: string;
  component: React.ComponentType<any>;
};

const fetchAuthenticatedUser = async () => {
  const authenticatedUser = await Auth.currentAuthenticatedUser();
  return authenticatedUser;
};

const fetchAgencyByUserName = async (userName: string) => {
  const { payload: agency } = await api.getAgencyByUserName(userName);
  return agency;
};

const AuthGuardRoute: FC<AuthGuardRouteProps> = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  component: Component,
  ...rest
}) => {
  const dispatch = useDispatch();

  const isAuthenticated: boolean = useSelector((state: ReduxApplicationState) => state.session.isAuthenticated);
  const agency = useSelector((state: ReduxApplicationState) => state.agency);
  const session = useSelector((state: ReduxApplicationState) => state.session.session);

  const [isUserAuthenticatedChecking, setIsUserAuthenticatedChecking] = useState<boolean>(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        setIsUserAuthenticatedChecking(true);
        const user = await Auth.currentSession();

        if (user && !session) {
          const newSession = {
            accessToken: user.getAccessToken().getJwtToken(),
            idToken: user.getIdToken().getJwtToken(),
            refreshToken: user.getRefreshToken().getToken(),
          };
          await dispatch(setSession(newSession));
          Cookies.set("ACCESS_TOKEN", newSession.accessToken);
        }

        if (session && !agency.name) {
          const authenticatedUser = await fetchAuthenticatedUser();
          const associatedAgency = await fetchAgencyByUserName(authenticatedUser.username);
          dispatch(setAgency(associatedAgency));
          dispatch(setUser(authenticatedUser.username));
          setIsUserAuthenticatedChecking(false);
        } else {
          setIsUserAuthenticatedChecking(false);
        }
      } catch (error) {
        if (error === "No current user") {
          setIsUserAuthenticatedChecking(false);
          // eslint-disable-next-line no-console
          console.log("User is not Authenticated!");
        } else {
          setIsUserAuthenticatedChecking(false);
          // eslint-disable-next-line no-console
          console.log("error:", error);
        }
      }
    };
    checkAuthStatus();
  }, [agency.name, dispatch, session]);

  return (
    <SentryRoute
      render={(props) =>
        isUserAuthenticatedChecking ? (
          <BaseLayout>
            <Loader />
          </BaseLayout>
        ) : isAuthenticated ? (
          <BaseLayout>
            <Component {...props} />
          </BaseLayout>
        ) : (
          <Redirect to={{ pathname: "/" }} />
        )
      }
      {...rest}
    />
  );
};

export default AuthGuardRoute;
